import { Search, Play, Square, RotateCcw, Trash2, Eye, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/components/ui/use-toast'
import {getAxiosInstance} from '../../network/axiosInstance'
import styles from '../../styles/Blueprints.module.css'
import { WorkflowRunWithDetails, WORKFLOW_STATUSES } from '../../types/blueprints'

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'running':
      return <Play className="h-4 w-4 text-blue-500" />
    case 'cancelled':
      return <Square className="h-4 w-4 text-gray-500" />
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'failed':
      return 'bg-red-100 text-red-800'
    case 'running':
      return 'bg-blue-100 text-blue-800'
    case 'cancelled':
      return 'bg-gray-100 text-gray-800'
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

interface WorkflowRunCardProps {
  workflowRun: WorkflowRunWithDetails
  onView: (workflowRun: WorkflowRunWithDetails) => void
  onCancel: (workflowRun: WorkflowRunWithDetails) => void
  onRerun: (workflowRun: WorkflowRunWithDetails) => void
  onDelete: (workflowRun: WorkflowRunWithDetails) => void
}

const WorkflowRunCard: React.FC<WorkflowRunCardProps> = ({
  workflowRun,
  onView,
  onCancel,
  onRerun,
  onDelete,
}) => {
  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A'
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    } else {
      return `${seconds}s`
    }
  }

  const canCancel = workflowRun.status === 'running' || workflowRun.status === 'pending'
  const canRerun = workflowRun.status === 'completed' || workflowRun.status === 'failed' || workflowRun.status === 'cancelled'

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="" hasCardAction={false}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold line-clamp-1">
              {workflowRun.blueprint.name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center gap-1">
                {getStatusIcon(workflowRun.status)}
                <Badge className={getStatusColor(workflowRun.status)}>
                  {workflowRun.status}
                </Badge>
              </div>
              <span className="text-sm text-muted-foreground">
                Progress: {workflowRun.progress}%
              </span>
            </div>
          </div>
        </div>      </CardHeader>

      <CardContent>
        <div className="space-y-4">          {/* Progress bar */}
          <div className={styles.progressBar}>
            <div 
              className={`${styles.progressFill} ${
                workflowRun.status === 'completed' 
                  ? styles.completed
                  : workflowRun.status === 'failed' 
                  ? styles.failed 
                  : styles.running
              }`}
              data-progress={Math.round(Math.min(100, Math.max(0, workflowRun.progress)))}
            />
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Started:</span>
              <div>{new Date(workflowRun.createdAt).toLocaleString()}</div>
            </div>
            <div>
              <span className="text-muted-foreground">Duration:</span>
              <div>{formatDuration(workflowRun.executionTimeMs)}</div>
            </div>
            {workflowRun.completedAt && (
              <div>
                <span className="text-muted-foreground">Completed:</span>
                <div>{new Date(workflowRun.completedAt).toLocaleString()}</div>
              </div>
            )}
            {workflowRun.errorMessage && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Error:</span>
                <div className="text-red-600 text-xs mt-1 line-clamp-2">
                  {workflowRun.errorMessage}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(workflowRun)}
              className="flex-1"
            >
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(workflowRun)}
              >
                <Square className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
            {canRerun && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRerun(workflowRun)}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Rerun
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(workflowRun)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const WorkflowRunsPage: React.FC = () => {
  const { toast } = useToast()
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRunWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Fetch workflow runs
  const fetchWorkflowRuns = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      if (searchTerm) params.append('search', searchTerm)
      if (statusFilter !== 'all') params.append('status', statusFilter)

      const response = await getAxiosInstance().get(`/api/workflow-runs?${params}`)
      if (!response.data) throw new Error('Failed to fetch workflow runs')
      
      const data = response.data
      setWorkflowRuns(data.workflowRuns || [])
    } catch (error) {
      console.error('Error fetching workflow runs:', error)
      toast({
        title: 'Error',
        description: 'Failed to load workflow runs',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkflowRuns()
  }, [searchTerm, statusFilter])

  const handleViewWorkflowRun = (_workflowRun: WorkflowRunWithDetails) => {
    // TODO: Open workflow run details dialog
    toast({
      title: 'Feature Coming Soon',
      description: 'Workflow run details will be available soon'
    })
  }

  const handleCancelWorkflowRun = async (workflowRun: WorkflowRunWithDetails) => {
    try {
      const response = await getAxiosInstance().post(`/api/workflow-runs/${workflowRun.id}/cancel`)
      
      if (!response.data) throw new Error('Failed to cancel workflow run')
      
      toast({
        title: 'Success',
        description: 'Workflow run cancelled successfully'
      })
      
      fetchWorkflowRuns() // Refresh list
    } catch (error) {
      console.error('Error cancelling workflow run:', error)
      toast({
        title: 'Error',
        description: 'Failed to cancel workflow run',
        variant: 'destructive'
      })
    }
  }

  const handleRerunWorkflow = (_workflowRun: WorkflowRunWithDetails) => {
    // TODO: Open rerun workflow dialog
    toast({
      title: 'Feature Coming Soon',
      description: 'Workflow rerun will be available soon'
    })
  }

  const handleDeleteWorkflowRun = async (workflowRun: WorkflowRunWithDetails) => {
    if (!confirm('Are you sure you want to delete this workflow run?')) {
      return
    }

    try {
      const response = await getAxiosInstance().delete(`/api/workflow-runs/${workflowRun.id}`)
      
      if (!response.data) throw new Error('Failed to delete workflow run')
      
      toast({
        title: 'Success',
        description: 'Workflow run deleted successfully'
      })
      
      fetchWorkflowRuns() // Refresh list
    } catch (error) {
      console.error('Error deleting workflow run:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete workflow run',
        variant: 'destructive'
      })
    }
  }

  const filteredWorkflowRuns = workflowRuns.filter(run => {
    if (statusFilter !== 'all' && run.status !== statusFilter) {
      return false
    }
    return true
  })

  const statusCounts = workflowRuns.reduce((acc, run) => {
    acc[run.status] = (acc[run.status] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Workflow Runs</h1>
          <p className="text-muted-foreground">
            Monitor and manage your blueprint workflow executions
          </p>
        </div>
        <div className="flex items-center gap-4">
          {Object.entries(statusCounts).map(([status, count]) => (
            <Badge key={status} variant="outline" className="flex items-center gap-1">
              {getStatusIcon(status)}
              {status}: {count}
            </Badge>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workflow runs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {WORKFLOW_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                <div className="flex items-center gap-2">
                  {getStatusIcon(status)}
                  {status}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Workflow Runs List */}
      {loading ? (
        <div className="text-center py-8">Loading workflow runs...</div>
      ) : filteredWorkflowRuns.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No workflow runs found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredWorkflowRuns.map((workflowRun) => (
            <WorkflowRunCard
              key={workflowRun.id}
              workflowRun={workflowRun}
              onView={handleViewWorkflowRun}
              onCancel={handleCancelWorkflowRun}
              onRerun={handleRerunWorkflow}
              onDelete={handleDeleteWorkflowRun}
            />
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="flex justify-center pt-4">
        <div className="text-sm text-muted-foreground">
          Showing {filteredWorkflowRuns.length} workflow run{filteredWorkflowRuns.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  )
}
